import { QueryClient } from '@tanstack/react-query';

/**
 * Configuración del QueryClient para React Query
 * 
 * Estrategia de caché:
 * - staleTime: Tiempo que los datos se consideran "frescos" (no se re-fetchean)
 * - cacheTime: Tiempo que los datos permanecen en caché después de no usarse
 * - retry: Número de reintentos en caso de fallo
 * - refetchOnWindowFocus: Re-fetchear al volver a la ventana (útil para datos dinámicos)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Datos "frescos" por 5 minutos (no hace requests innecesarios)
      staleTime: 5 * 60 * 1000, // 5 minutos
      
      // Mantener en caché por 10 minutos después de no usarse
      cacheTime: 10 * 60 * 1000, // 10 minutos
      
      // Reintentar 2 veces en caso de fallo
      retry: 2,
      
      // Delay entre reintentos (exponencial)
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // No re-fetchear al volver al tab (para no molestar al usuario)
      refetchOnWindowFocus: false,
      
      // Re-fetchear al reconectar (útil en móviles)
      refetchOnReconnect: true,
      
      // No re-fetchear al montar si los datos están frescos
      refetchOnMount: false,
    },
    mutations: {
      // Reintentar mutations una vez
      retry: 1,
    },
  },
});

/**
 * Configuración específica por tipo de dato
 */
export const queryKeys = {
  // Sucursales (cambian raramente)
  shopBranches: (subdomain) => ['shopBranches', subdomain],
  
  // Configuración de sucursal (puede cambiar durante el día)
  branchConfig: (branchId) => ['branchConfig', branchId],
  
  // Features de sucursal (raramente cambia)
  branchFeatures: (branchId) => ['branchFeatures', branchId],
  
  // Categorías y productos (cambian con frecuencia moderada)
  categories: (branchId) => ['categories', branchId],
  
  // Redes sociales (raramente cambian)
  socialMedia: (branchId) => ['socialMedia', branchId],
  
  // Banners (pueden cambiar semanalmente)
  banners: (branchId) => ['banners', branchId],
  
  // Logo (raramente cambia)
  logo: (branchId) => ['logo', branchId],
  
  // Color de categoría (raramente cambia)
  categoryColor: (branchId) => ['categoryColor', branchId],
};

/**
 * Tiempos de stale específicos por tipo de dato
 */
export const staleTimes = {
  // Datos que casi nunca cambian
  static: 60 * 60 * 1000, // 1 hora
  
  // Datos que cambian ocasionalmente
  moderate: 10 * 60 * 1000, // 10 minutos
  
  // Datos que cambian frecuentemente
  dynamic: 2 * 60 * 1000, // 2 minutos
  
  // Datos en tiempo real
  realtime: 30 * 1000, // 30 segundos
  
  // Datos que cambian muy raramente (páginas siguientes de paginación)
  long: 30 * 60 * 1000, // 30 minutos
};
